import React, { useEffect, useRef } from 'react'
import { Card } from '../Cards'
import { Spinner } from '../Loading/Spinner'
import { Stack } from '../Stack'
import { CARD_TYPE } from '@/constants'
import { useIntersect } from '@/hooks/utils/useIntersect'

import classNames from 'classnames'
import classes from './styles.module.scss'

interface ContentGridProps {
  items?: any[]
  isPlaceholderData: boolean
  isFetching: boolean
  handleNextPage?: () => void
  title?: string
  hasNextPage?: boolean
  isLoading: boolean
  cardOptions?: { cardType: string, imageCut?: string }
  hasInfiniteScroll?: boolean
  columnWidth?: string
}

export const ContentGrid: React.FC<ContentGridProps> = ({
  hasNextPage,
  isLoading,
  items = [],
  isPlaceholderData,
  isFetching,
  title,
  handleNextPage,
  cardOptions,
  hasInfiniteScroll = true,
  columnWidth = '250px'
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const { reset } = useIntersect(sentinelRef, {
    rootMargin: '100px',
    threshold: 0.1,
    once: true,
    enabled: hasNextPage && !isFetching && !isLoading,
    onIntersect: () => {
      handleNextPage?.()
    },
  })

  useEffect(() => {
    if (!isFetching) {
      reset()
    }
  }, [isFetching, reset])

  return (
    <section className={classes.container}>
      {title && (
        <h1
          className={classNames({
            [classes.title]: true,
            [classes.title__placeholder]: isPlaceholderData,
          })}
        >
          {title}
        </h1>
      )}
      <div
        className={classes.content}
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(${columnWidth}, 1fr))`,
        }}
      >
        {items.map((item, index) => (
          <div key={item?._id ?? `item-${index}`}>
            <Card
              item={item}
              options={cardOptions || { cardType: CARD_TYPE.HORIZONTAL }}
              isPlaceholder={isPlaceholderData}
            />
          </div>
        ))}
      </div>
      <Stack justify='center' align='center' my='xxl'>
        {(isFetching && hasInfiniteScroll) ? (
          <Spinner />
        ) : (
          <div
            ref={sentinelRef}
            style={{ height: '1px' }}
          />
        )}
      </Stack>
    </section>
  )
}
